import { Box, Stack, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import type { MutableRefObject, Ref } from "react";
import {
	type Control,
	Controller,
	type UseFormGetValues,
	type UseFormReset,
	type UseFormGetFieldState,
	type UseFormSetValue,
} from "react-hook-form";

import type { SectionSpecItem } from "@/shared/models/SectionSpec";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import {
	formatMeasurement,
	validateMeasurement,
} from "@/shared/utils/measurementPrecision";

import {
	type InspectionWithValues,
	statusOf,
} from "@/pages/products/test-shack/models/Inspection";
import { InspectionHeatField } from "@/pages/products/test-shack/shared/components/InspectionHeatField";
import type { InspectionFormValues } from "@/pages/products/test-shack/models/InspectionForm";
import {
	getFormValues,
	parse,
	REQUIRED_SAMPLE_AT_MESSAGE,
	REQUIRED_SECTION_MESSAGE,
	statusColor,
} from "@/pages/products/test-shack/utils/inspectionFormValues";
import { useLegDeltas } from "../../hooks/useLegDeltas";

interface InspectionIdentityFieldsProps {
	assetId: number;
	sectionId: number | null;
	control: Control<InspectionFormValues>;
	editing: InspectionWithValues | null;
	saving: boolean;
	serverTimeZone: string;
	itemKeys: string[];
	targetLbsPerFoot: number | null;
	minLbsPerFoot: number | null;
	maxLbsPerFoot: number | null;
	getValues: UseFormGetValues<InspectionFormValues>;
	reset: UseFormReset<InspectionFormValues>;
	setValue: UseFormSetValue<InspectionFormValues>;
	getFieldState: UseFormGetFieldState<InspectionFormValues>;
	firstFieldRef: Ref<HTMLInputElement>;
	hasResolvedCurrentSectionRef: MutableRefObject<boolean>;
	onSectionChange: (sectionId: number | null) => void;
	orientation?: "column" | "row";
}

interface InspectionFormFieldsProps extends InspectionIdentityFieldsProps {
	dimensions: SectionSpecItem[];
	legDeltaMax: number | null;
}

interface LegDeltaRowProps {
	label: string;
	value: number | null;
	max: number | null;
	isOutOfTolerance: boolean;
}

const LegDeltaRow = ({
	label,
	value,
	max,
	isOutOfTolerance,
}: LegDeltaRowProps) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: "minmax(0, 1fr) auto auto",
			gap: 1,
			alignItems: "baseline",
		}}
	>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
		<Typography
			variant="body2"
			fontFamily="monospace"
			color={isOutOfTolerance ? "error.main" : statusColor("In")}
		>
			{value !== null ? formatMeasurement(value) : "-"}
		</Typography>
		<Typography variant="caption" color="text.secondary">
			max {max !== null ? formatMeasurement(max) : "-"}
		</Typography>
	</Box>
);

export const InspectionCommentsField = ({
	control,
	fill,
}: {
	control: Control<InspectionFormValues>;
	fill?: boolean;
}) => (
	<Controller
		name="comments"
		control={control}
		render={({ field }) => (
			<TextField
				{...field}
				label="Comments"
				margin="none"
				size="small"
				multiline
				rows={fill ? 3 : 6}
				fullWidth
				sx={{
					flex: fill ? "1 1 auto" : "0 1 auto",
					minHeight: fill ? 96 : 56,
					height: fill ? undefined : 130,
					"& .MuiInputBase-root": {
						alignItems: "flex-start",
						height: "100%",
					},
					"& textarea": { height: "100% !important", overflow: "auto" },
				}}
			/>
		)}
	/>
);

export const InspectionIdentityFields = ({
	assetId,
	sectionId,
	control,
	editing,
	saving,
	serverTimeZone,
	itemKeys,
	targetLbsPerFoot,
	minLbsPerFoot,
	maxLbsPerFoot,
	getValues,
	reset,
	setValue,
	getFieldState,
	firstFieldRef,
	hasResolvedCurrentSectionRef,
	onSectionChange,
	orientation = "column",
}: InspectionIdentityFieldsProps) => (
	<Box
		sx={{
			// SectionSelector's TextField inherits themeMESF's dense margin; keep it aligned with siblings.
			"& .MuiTextField-root": { my: 0 },
			...(orientation === "row"
				? {
						display: "grid",
						flexShrink: 0,
						gap: 1.5,
						gridTemplateColumns: {
							xs: "minmax(0, 1fr)",
							sm: "repeat(2, minmax(0, 1fr))",
						},
						minWidth: 0,
					}
				: {
						display: "flex",
						flexDirection: "column",
						gap: 1.5,
						minWidth: 0,
					}),
		}}
	>
		<Controller
			name="inspectedAt"
			control={control}
			rules={{
				validate: (value: Moment | null) =>
					value?.isValid() === true || REQUIRED_SAMPLE_AT_MESSAGE,
			}}
			render={({ field, fieldState }) => (
				<DateTimePicker
					label="Sample At"
					disableFuture
					value={field.value}
					inputRef={field.ref}
					onChange={field.onChange}
					timezone={serverTimeZone}
					format="MM/DD/YYYY HH:mm"
					views={["year", "month", "day", "hours", "minutes"]}
					timeSteps={{ minutes: 1 }}
					ampm={false}
					disabled={saving}
					slotProps={{
						textField: {
							margin: "none",
							inputProps: { "data-test-id": "inspection-sampled-at-input" },
							fullWidth: true,
							required: true,
							size: "small",
							onBlur: field.onBlur,
							error: Boolean(fieldState.error),
							helperText: fieldState.error?.message,
						},
					}}
				/>
			)}
		/>
		<Controller
			name="sectionId"
			control={control}
			rules={{ required: REQUIRED_SECTION_MESSAGE }}
			render={({ field, fieldState }) => (
				<SectionSelector
					value={field.value}
					onChange={(value) => {
						const previousValues = getValues();
						if (!editing) hasResolvedCurrentSectionRef.current = true;
						// The heat list is per section: a kept heat would be saved
						// while the field shows empty.
						if (editing && value !== previousValues.sectionId)
							setValue("heatId", null, { shouldDirty: true });
						field.onChange(value);
						onSectionChange(value);
						if (!editing && value !== previousValues.sectionId) {
							// Keep a time the user picked; otherwise start from the current minute.
							const { isDirty } = getFieldState("inspectedAt");
							reset(getFormValues(null, value, serverTimeZone, itemKeys));
							if (isDirty)
								setValue("inspectedAt", previousValues.inspectedAt, {
									shouldDirty: true,
								});
						}
					}}
					assetId={assetId}
					size="small"
					required
					disabled={saving}
					fieldError={fieldState.error}
					sortBy={"recent"}
					hideTph
				/>
			)}
		/>
		<Box sx={{ "& .MuiAutocomplete-root": { width: "100%" } }}>
			<InspectionHeatField
				control={control}
				assetId={assetId}
				sectionId={sectionId}
				inputRef={firstFieldRef}
			/>
		</Box>
		<Controller
			name="weight"
			control={control}
			rules={{ validate: (raw) => validateMeasurement(raw).message ?? true }}
			render={({ field, fieldState }) => {
				const message =
					fieldState.error?.message ?? validateMeasurement(field.value).message;
				const status = statusOf(
					parse(field.value),
					minLbsPerFoot,
					maxLbsPerFoot,
				);
				return (
					<TextField
						{...field}
						label="Weight lb/ft"
						margin="none"
						size="small"
						sx={{ width: "100%" }}
						helperText={
							message ??
							(targetLbsPerFoot !== null
								? `target ${formatMeasurement(targetLbsPerFoot)}`
								: "no target loaded")
						}
						error={
							Boolean(fieldState.error) ||
							message != null ||
							status === "Over" ||
							status === "Under"
						}
						slotProps={{ htmlInput: { inputMode: "decimal" } }}
					/>
				);
			}}
		/>
	</Box>
);

const LegDeltaRows = ({
	control,
	dimensions,
	legDeltaMax,
}: {
	control: Control<InspectionFormValues>;
	dimensions: SectionSpecItem[];
	legDeltaMax: number | null;
}) => {
	const { topDelta, bottomDelta, topOut, bottomOut } = useLegDeltas(
		control,
		dimensions,
		legDeltaMax,
	);
	return (
		<>
			<LegDeltaRow
				label="D. TOP LEGS"
				value={topDelta}
				max={legDeltaMax}
				isOutOfTolerance={topOut}
			/>
			<LegDeltaRow
				label="D. BOT. LEGS"
				value={bottomDelta}
				max={legDeltaMax}
				isOutOfTolerance={bottomOut}
			/>
		</>
	);
};

export const InspectionFormFields = ({
	dimensions,
	legDeltaMax,
	...identityProps
}: InspectionFormFieldsProps) => (
	<Stack spacing={1.5} sx={{ alignSelf: "stretch", minHeight: 0, minWidth: 0 }}>
		<InspectionIdentityFields {...identityProps} />
		<LegDeltaRows
			control={identityProps.control}
			dimensions={dimensions}
			legDeltaMax={legDeltaMax}
		/>
		<InspectionCommentsField control={identityProps.control} />
	</Stack>
);
